import { Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { Button } from '#/shared/ui/button'
import { Checkbox } from '#/shared/ui/checkbox'
import { Input } from '#/shared/ui/input'

import { AUTH_CARD_CLASS, AuthCardHeader } from './auth-screen'
import {
  passkeyCancelled,
  passkeyErrorMessage,
  passkeysSupported,
  useFinishPasskeySetup,
} from './passkeys'
import { RecoveryCodeList } from './recovery-codes'
import { clearPendingSetup, readPendingSetup, type PendingSetup } from './session'

const WHY: Record<PendingSetup['reason'], string> = {
  required: 'Your password is right. This console now signs in with passkeys — add one to finish.',
  recovery: 'Recovery code accepted. Add a new passkey to get back in.',
}

function SaveCodes({ codes, email }: { codes: string[]; email: string }) {
  const navigate = useNavigate()
  const [saved, setSaved] = useState(false)

  return (
    <div className={AUTH_CARD_CLASS}>
      <AuthCardHeader
        title="Save your recovery codes"
        subtitle="If you lose every passkey, one code lets you in once. They are not shown again."
      />
      <RecoveryCodeList codes={codes} email={email} />
      <label className="flex items-center gap-2 text-sm text-gray-900">
        <Checkbox checked={saved} onCheckedChange={(next) => setSaved(next === true)} />I have saved
        these codes somewhere safe
      </label>
      <Button
        type="button"
        size="lg"
        disabled={!saved}
        className="w-full"
        onClick={() => void navigate({ to: '/' })}
      >
        Open the console
      </Button>
    </div>
  )
}

/**
 * The step between a right password and an open console.
 *
 * Reached only after the password (or a recovery code) was accepted while
 * passkeys are required. The console opens the moment the passkey is saved;
 * when the admin had no recovery codes left, the new set is shown first,
 * because this is the only moment they exist in readable form.
 */
export function PasskeySetup() {
  const navigate = useNavigate()
  const [pending] = useState(readPendingSetup)
  const finish = useFinishPasskeySetup()
  const [name, setName] = useState('')
  const [codes, setCodes] = useState<string[] | null>(null)
  const supported = passkeysSupported()

  if (codes && pending) return <SaveCodes codes={codes} email={pending.email} />

  const problem = !supported
    ? 'This browser cannot use passkeys. Open the console in a current Chrome, Safari, Edge or Firefox.'
    : finish.isError && !passkeyCancelled(finish.error)
      ? passkeyErrorMessage(finish.error)
      : null

  const submit = () =>
    finish.mutate(
      { name },
      { onSuccess: (recovery) => (recovery ? setCodes(recovery) : void navigate({ to: '/' })) },
    )

  return (
    <form
      noValidate
      className={AUTH_CARD_CLASS}
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <AuthCardHeader title="Add a passkey" subtitle={pending ? WHY[pending.reason] : ''} />
      <div className="flex flex-col gap-1">
        <span className="text-label text-gray-500">Signing in as</span>
        <span className="text-sm font-medium text-gray-900">{pending?.email}</span>
      </div>
      <Input
        label="Name it after the device or key"
        maxLength={64}
        placeholder="MacBook Touch ID"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      {problem ? (
        <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-500">
          {problem}
        </p>
      ) : null}
      <Button type="submit" size="lg" loading={finish.isPending} disabled={!supported}>
        Add passkey
      </Button>
      <Link
        to="/login"
        onClick={clearPendingSetup}
        className="text-label -mt-2 text-center text-gray-500 hover:text-gray-900"
      >
        Back to sign in
      </Link>
    </form>
  )
}
